import React, { useState, useEffect } from 'react';
import { Users, BookOpen, Award, TrendingUp, Plus, Calendar, Filter, Download, Eye, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const ManagerTrainingDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [employees, setEmployees] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [progress, setProgress] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAssignTraining, setShowAssignTraining] = useState(false);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const TRAINING_API_BASE = '/api/documents/training';

  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('access_token');
  
    const response = await fetch(`${TRAINING_API_BASE}/${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      },
      credentials: 'include',
      ...options
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [isAuthenticated, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading training dashboard data...');

      const healthData = await apiCall('health');
      console.log('Training API health check:', healthData);

      const [employeesData, analyticsData, progressData, modulesData] = await Promise.all([
        apiCall('employees'),
        apiCall('analytics'),
        apiCall('progress'),
        apiCall('admin/modules')
      ]);

      console.log('Training data loaded:', {
        employees: employeesData.success,
        analytics: analyticsData.success,
        progress: progressData.success,
        modules: modulesData.success
      });

      if (employeesData.success) setEmployees(employeesData.employees || []);
      if (analyticsData.success) setAnalytics(analyticsData);
      if (progressData.success) setProgress(progressData.progress || []);
      if (modulesData.success) setModules(modulesData.modules || []);

    } catch (error) {
      console.error('Failed to load training dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const StatsGrid = () => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '24px',
      marginBottom: '32px'
    }}>
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderLeft: '4px solid #3b82f6'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 8px 0'
            }}>Total Staff</p>
            <p style={{
              fontSize: '30px',
              fontWeight: 'bold',
              color: '#111827',
              margin: 0
            }}>{analytics?.overall?.total_staff || employees.length}</p>
          </div>
          <Users color="#3b82f6" size={32} />
        </div>
        <p style={{
          fontSize: '14px',
          color: '#059669',
          marginTop: '8px',
          margin: 0
        }}>Active employees</p>
      </div>

      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderLeft: '4px solid #10b981'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 8px 0'
            }}>Completed</p>
            <p style={{
              fontSize: '30px',
              fontWeight: 'bold',
              color: '#111827',
              margin: 0
            }}>{analytics?.overall?.passed_assessments || 0}</p>
          </div>
          <Award color="#10b981" size={32} />
        </div>
        <p style={{
          fontSize: '14px',
          color: '#059669',
          marginTop: '8px',
          margin: 0
        }}>
          {analytics?.overall?.total_assessments > 0 
            ? Math.round((analytics.overall.passed_assessments / analytics.overall.total_assessments) * 100)
            : 0}% success rate
        </p>
      </div>

      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderLeft: '4px solid #f59e0b'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 8px 0'
            }}>Avg Score</p>
            <p style={{
              fontSize: '30px',
              fontWeight: 'bold',
              color: '#111827',
              margin: 0
            }}>
              {analytics?.overall?.avg_score ? Math.round(analytics.overall.avg_score) : 0}%
            </p>
          </div>
          <TrendingUp color="#f59e0b" size={32} />
        </div>
        <p style={{
          fontSize: '14px',
          color: '#059669',
          marginTop: '8px',
          margin: 0
        }}>Overall performance</p>
      </div>

      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        borderLeft: '4px solid #8b5cf6'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <p style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: '0 0 8px 0'
            }}>Modules</p>
            <p style={{
              fontSize: '30px',
              fontWeight: 'bold',
              color: '#111827',
              margin: 0
            }}>{modules.length}</p>
          </div>
          <BookOpen color="#8b5cf6" size={32} />
        </div>
        <p style={{
          fontSize: '14px',
          color: '#059669',
          marginTop: '8px',
          margin: 0
        }}>Available training</p>
      </div>
    </div>
  );

  // OVERVIEW TAB - Quick Actions and Summary
  const OverviewTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#111827',
        margin: 0
      }}>Dashboard Overview</h2>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px'
      }}>
        {/* Left Column - Quick Actions */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button
              onClick={() => setActiveTab('employees')}
              style={{
                width: '100%',
                padding: '16px',
                textAlign: 'left',
                background: '#eff6ff',
                border: '1px solid #dbeafe',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#dbeafe'}
              onMouseOut={(e) => e.currentTarget.style.background = '#eff6ff'}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Users size={32} color="#3b82f6" style={{ marginRight: '12px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: '500', color: '#111827' }}>Manage Employees</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Add and manage staff members</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/employeeportal')}
              style={{
                width: '100%',
                padding: '16px',
                textAlign: 'left',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#dcfce7'}
              onMouseOut={(e) => e.currentTarget.style.background = '#f0fdf4'}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <BookOpen size={32} color="#10b981" style={{ marginRight: '12px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: '500', color: '#111827' }}>Employee Training Portal</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Access the employee training interface</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('analytics')}
              style={{
                width: '100%',
                padding: '16px',
                textAlign: 'left',
                background: '#faf5ff',
                border: '1px solid #e9d5ff',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f3e8ff'}
              onMouseOut={(e) => e.currentTarget.style.background = '#faf5ff'}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Award size={32} color="#8b5cf6" style={{ marginRight: '12px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: '500', color: '#111827' }}>View All Modules</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>Browse {modules.length} available training modules</div>
                </div>
              </div>
            </button>
            
            <div style={{
              padding: '16px',
              background: '#fffbeb',
              borderRadius: '8px',
              border: '1px solid #fde68a'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Award size={32} color="#f59e0b" style={{ marginRight: '12px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: '500', color: '#111827' }}>Platform Status</div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    {error ? (
                      <span style={{ color: '#dc2626' }}>⚠️ {error}</span>
                    ) : modules.length === 0 ? (
                      <span style={{ color: '#ea580c' }}>⚠️ No training modules found</span>
                    ) : (
                      <span style={{ color: '#059669' }}>✅ Connected • {modules.length} modules</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Quick Training Assignment */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '16px',
            margin: '0 0 16px 0'
          }}>🚀 Quick Training Assignment</h3>
          
          {employees.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {employees.map((employee) => (
                <div key={employee.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      {employee.first_name?.[0] || 'U'}{employee.last_name?.[0] || 'U'}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500', color: '#111827', fontSize: '14px' }}>
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                       {employee.assigned_count || 0} assigned • {employee.passed_assessments || 0}/{employee.total_assessments || 0} completed
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      console.log('Quick assignment clicked for employee:', employee.id);
                      setShowAssignTraining(employee.id);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#10b981',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: '500',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#059669'}
                    onMouseOut={(e) => e.target.style.background = '#10b981'}
                  >
                    Assign Training
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#6b7280'
            }}>
              <Users size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
              <p style={{ margin: '0 0 16px 0' }}>No employees added yet</p>
              <button
                onClick={() => setActiveTab('employees')}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Add Your First Employee
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // EMPLOYEES TAB - Staff Management
  const EmployeesTab = () => {
    const [newEmployee, setNewEmployee] = useState({
      first_name: '', last_name: '', email: '', phone: '', position: '', login_pin: ''
    });

    const addEmployee = async () => {
      try {
        const data = await apiCall('employees', {
          method: 'POST',
          body: JSON.stringify(newEmployee)
        });
        
        if (data.success) {
          setShowAddEmployee(false);
          setNewEmployee({ first_name: '', last_name: '', email: '', phone: '', position: '', login_pin: '' });
          loadDashboardData();
        } else {
          alert(data.error || 'Failed to add employee');
        }
      } catch (error) {
        alert('Failed to add employee: ' + error.message);
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>Staff Management</h2>
          <button
            onClick={() => setShowAddEmployee(true)}
            style={{
              background: '#3b82f6',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563eb'}
            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
          >
            <Plus size={20} />
            Add Employee
          </button>
        </div>

        {showAddEmployee && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            zIndex: 50
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '448px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '16px',
                color: '#111827'
              }}>Add New Employee</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                    style={{
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                    style={{
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <input
                  type="text"
                  placeholder="Phone"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <input
                  type="text"
                  placeholder="Position"
                  value={newEmployee.position}
                  onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <input
                  type="text"
                  placeholder="6-digit PIN"
                  value={newEmployee.login_pin}
                  onChange={(e) => setNewEmployee({...newEmployee, login_pin: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  maxLength={6}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => setShowAddEmployee(false)}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    background: '#f3f4f6',
                    color: '#374151',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={addEmployee}
                  style={{
                    flex: 1,
                    padding: '8px 16px',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Add Employee
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '24px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              margin: 0
            }}>All Employees ({employees.length})</h3>
          </div>
          <div>
            {employees.length > 0 ? employees.map((employee, index) => (
              <div 
                key={employee.id} 
                style={{
                  padding: '24px',
                  borderBottom: index < employees.length - 1 ? '1px solid #e5e7eb' : 'none',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseOut={(e) => e.currentTarget.style.background = 'white'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '18px'
                    }}>
                      {employee.first_name?.[0] || 'U'}{employee.last_name?.[0] || 'U'}
                    </div>
                    <div>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#111827',
                        margin: '0 0 4px 0'
                      }}>
                        {employee.first_name} {employee.last_name}
                      </h4>
                      <p style={{
                        color: '#6b7280',
                        margin: '0 0 4px 0',
                        fontSize: '14px'
                      }}>{employee.position || 'Staff Member'}</p>
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginTop: '4px'
                      }}>
                        {employee.assigned_count || 0} assigned • {employee.passed_assessments || 0}/{employee.total_assessments || 0} completed • {employee.certificates_earned || 0} certificates
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>Overall Progress</div>
                    <div style={{
                      width: '128px',
                      background: '#e5e7eb',
                      borderRadius: '9999px',
                      height: '12px'
                    }}>
                      <div 
                        style={{
                          background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                          height: '12px',
                          borderRadius: '9999px',
                          width: `${employee.total_assessments > 0 ? (employee.passed_assessments / employee.total_assessments) * 100 : 0}%`
                        }}
                      />
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#111827',
                      marginTop: '4px'
                    }}>
                      {employee.total_assessments > 0 ? Math.round((employee.passed_assessments / employee.total_assessments) * 100) : 0}%
                    </div>
                    
                    <button
                      onClick={() => {
                        console.log('Assignment button clicked for employee:', employee.id);
                        setShowAssignTraining(employee.id);
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <Plus size={16} />
                      Assign Training
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{
                padding: '32px',
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <Users size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
                <p style={{ margin: 0 }}>No employees added yet. Click "Add Employee" to get started.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ANALYTICS TAB - Detailed Module List
  const AnalyticsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#111827',
        margin: 0
      }}>Training Modules</h2>
      
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: 0
          }}>Available Training Modules ({modules.length})</h3>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {modules.length > 0 ? modules.map((module, index) => (
            <div key={module.id || index} style={{
              borderBottom: index < modules.length - 1 ? '1px solid #f3f4f6' : 'none',
              paddingBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    fontWeight: '500',
                    color: '#111827',
                    margin: '0 0 4px 0',
                    fontSize: '16px'
                  }}>{module.title || 'Training Module'}</h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    textTransform: 'capitalize',
                    margin: '0 0 4px 0'
                  }}>{module.package_level || 'basic'} package</p>
                  {module.description && (
                    <p style={{
                      fontSize: '13px',
                      color: '#9ca3af',
                      margin: 0,
                      lineHeight: '1.4'
                    }}>{module.description}</p>
                  )}
                </div>
                <span style={{
                  padding: '4px 12px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: '500',
                  marginLeft: '16px',
                  whiteSpace: 'nowrap'
                }}>
                  {module.questions_count || 0} questions
                </span>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '16px',
                fontSize: '14px',
                marginTop: '12px'
              }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Passing Score</div>
                  <div style={{ fontWeight: '600', color: '#059669' }}>
                    {module.passing_score || 70}%
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Time Estimate</div>
                  <div style={{ fontWeight: '600', color: '#2563eb' }}>
                    {module.time_estimate || 15} min
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Module ID</div>
                  <div style={{ fontWeight: '600', color: '#7c3aed' }}>
                    #{module.id}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Status</div>
                  <div style={{ 
                    fontWeight: '600', 
                    color: module.is_active ? '#059669' : '#dc2626' 
                  }}>
                    {module.is_active ? '✓ Active' : '✗ Inactive'}
                  </div>
                </div>
              </div>
            </div>
          )) : (
            <div style={{
              textAlign: 'center',
              color: '#6b7280',
              padding: '32px'
            }}>
              <BookOpen size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
              <p style={{ margin: 0 }}>No training modules available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ASSIGN TRAINING MODAL
  const AssignTrainingModal = ({ employeeId, onClose }) => {
    const [selectedModules, setSelectedModules] = useState([]);
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState('medium');
    const [showOnlyRequired, setShowOnlyRequired] = useState(false);

    const getFilteredModules = () => {
      if (showOnlyRequired) {
        return modules.filter(module => 
          module.title.includes('H&S-') && 
          ['H&S-001', 'H&S-002', 'H&S-003', 'H&S-007', 'H&S-015'].some(code => module.title.includes(code))
        );
      }
      return modules;
    };

    const selectedEmployee = employees.find(e => e.id === employeeId);

    const assignTraining = async () => {
      try {
        const data = await apiCall('assign', {
          method: 'POST',
          body: JSON.stringify({
            employee_id: employeeId,
            employee_ids: [employeeId],
            module_ids: selectedModules,
            due_date: dueDate || null,
            priority
          })
        });
        
        console.log('Assignment response:', data);
        
        if (data.success) {
          onClose();
          loadDashboardData();
          alert(`Successfully assigned ${selectedModules.length} training module(s)!`);
        } else {
          alert(data.error || 'Failed to assign training');
        }
      } catch (error) {
        console.error('Assignment error:', error);
        alert('Failed to assign training: ' + error.message);
      }
    };

    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 50
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '672px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: '#111827'
          }}>Assign Training Modules</h3>
          
          {selectedEmployee && (
            <p style={{ 
              color: '#6b7280', 
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              Assigning to: <strong>{selectedEmployee.first_name} {selectedEmployee.last_name}</strong>
            </p>
          )}
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowOnlyRequired(false)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  background: !showOnlyRequired ? '#3b82f6' : '#e5e7eb',
                  color: !showOnlyRequired ? 'white' : '#374151'
                }}
              >
                All Modules ({modules.length})
              </button>
              <button
                onClick={() => setShowOnlyRequired(true)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  background: showOnlyRequired ? '#10b981' : '#e5e7eb',
                  color: showOnlyRequired ? 'white' : '#374151'
                }}
              >
                Core H&S Only
              </button>
              <button
                onClick={() => setSelectedModules(getFilteredModules().map(m => m.id))}
                style={{
                  padding: '6px 12px',
                  background: '#ea580c',
                  color: 'white',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Select All Visible
              </button>
              <button
                onClick={() => setSelectedModules([])}
                style={{
                  padding: '6px 12px',
                  background: '#6b7280',
                  color: 'white',
                  borderRadius: '9999px',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Clear Selection
              </button>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Select Training Modules 
                <span style={{ color: '#6b7280' }}> ({selectedModules.length} selected)</span>
              </label>
              <div style={{
                maxHeight: '240px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {getFilteredModules().map((module) => (
                    <label 
                      key={module.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedModules.includes(module.id) ? '#eff6ff' : 'transparent',
                        border: selectedModules.includes(module.id) ? '1px solid #bfdbfe' : '1px solid transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(module.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedModules([...selectedModules, module.id]);
                          } else {
                            setSelectedModules(selectedModules.filter(id => id !== module.id));
                          }
                        }}
                        style={{
                          marginTop: '4px',
                          accentColor: '#3b82f6',
                          width: '16px',
                          height: '16px'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontWeight: '500',
                            fontSize: '14px',
                            color: '#111827'
                          }}>{module.title}</span>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '9999px',
                            fontSize: '11px',
                            background: module.title.includes('H&S-') ? '#fee2e2' : '#dbeafe',
                            color: module.title.includes('H&S-') ? '#991b1b' : '#1e40af'
                          }}>
                            {module.title.includes('H&S-') ? 'Required' : 'Optional'}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6b7280',
                          marginTop: '4px'
                        }}>
                          {module.time_estimate || 15}min • {module.questions_count || 0} questions • {module.passing_score || 70}% to pass
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>Due Date (Optional)</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
              </div>
            </div>

            <div style={{
              background: '#eff6ff',
              border: '1px solid #dbeafe',
              borderRadius: '8px',
              padding: '12px'
            }}>
              <div style={{
                fontWeight: '500',
                color: '#1e40af',
                fontSize: '14px',
                marginBottom: '8px'
              }}>💡 Smart Recommendations:</div>
              <div style={{
                color: '#1e40af',
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                <div>• <strong>New starters:</strong> Assign core H&S modules first</div>
                <div>• <strong>Equipment users:</strong> Include UV Safety, Electrical Safety, PPE Usage</div>
                <div>• <strong>Senior staff:</strong> Add management and customer service modules</div>
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: '#f3f4f6',
                color: '#374151',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={assignTraining}
              disabled={selectedModules.length === 0}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: selectedModules.length === 0 ? '#d1d5db' : '#3b82f6',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
                cursor: selectedModules.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '500'
              }}
            >
              Assign {selectedModules.length} Module{selectedModules.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '64px',
            height: '64px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{
            color: '#6b7280',
            fontSize: '18px',
            margin: 0
          }}>Loading Training Dashboard...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f9fafb',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        color: 'white'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '32px 16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => navigate('/documents')}
              style={{
                padding: '8px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{
                fontSize: '30px',
                fontWeight: 'bold',
                margin: '0 0 8px 0'
              }}>Training Management Dashboard</h1>
              <p style={{
                color: '#bfdbfe',
                margin: 0
              }}>Monitor and manage your team's training progress</p>
            </div>
          </div>
          
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <p style={{
                color: '#fecaca',
                margin: '0 0 4px 0'
              }}>⚠️ {error}</p>
              <p style={{
                color: '#fca5a5',
                fontSize: '14px',
                margin: 0
              }}>
                Please ensure you're logged in and the training backend is running.
              </p>
            </div>
          )}
        </div>
      </div>

      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '32px 16px'
      }}>
        <StatsGrid />

        <div style={{
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '32px'
        }}>
          <nav style={{ display: 'flex', gap: '32px' }}>
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'employees', label: 'Employees', icon: Users },
              { id: 'analytics', label: 'Modules', icon: BookOpen }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingBottom: '16px',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  fontWeight: '500',
                  fontSize: '14px',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: activeTab === tab.id ? '#3b82f6' : 'transparent',
                  cursor: 'pointer'
                }}
              >
                <tab.icon size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'employees' && <EmployeesTab />}
        {activeTab === 'analytics' && <AnalyticsTab />}

        {showAssignTraining && (
          <AssignTrainingModal
            employeeId={showAssignTraining}
            onClose={() => setShowAssignTraining(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ManagerTrainingDashboard;